import type { ComponentType, ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import Link from '@/components/ui/Link';
import { cn } from '@/lib/utils';

export type KpiTone = 'neutral' | 'brand' | 'amber' | 'rose' | 'sky' | 'emerald';

export interface KpiMeter {
  value: number;                    // 0..100
  tone?: 'brand' | 'amber' | 'rose' | 'emerald';
  kind?: 'bar' | 'ring';
}

export interface KpiStatProps extends VariantProps<typeof cardVariants> {
  icon: ComponentType<{ size?: number; weight?: 'bold' | 'regular' | 'duotone' | 'fill'; className?: string }>;
  /** @deprecated prefer `tone` */
  iconClass?: string;
  tone?: KpiTone;
  label: string;
  /** Plain strings get sign-based coloring when `valueFont="sans"`; rich nodes (e.g. MoneyValue) render as-is in ink. */
  value: ReactNode;
  /** Money/counts read best in mono (default); percentages and ratios in bold sans (matches the redesign hero KPIs). */
  valueFont?: 'mono' | 'sans';
  detail?: ReactNode;
  hideDetailOnMobile?: boolean;
  info?: ReactNode;
  meter?: KpiMeter;
  href?: string;
  onClick?: () => void;
  ariaLabel?: string;
  className?: string;
  children?: ReactNode;
}

const cardVariants = cva(
  'relative flex h-full min-h-[100px] flex-col rounded-3xl p-3 transition-[box-shadow,border-color] sm:min-h-[124px] sm:p-4',
  {
    variants: {
      variant: {
        default: 'border border-border-subtle bg-surface shadow-sm',
        // Hero differentiates only via stronger border + shadow — no color accent on the card itself.
        hero:    'border border-border-strong bg-surface shadow-md',
        // Redesign card: roomier (taller + more padding), 18px radius, soft shadow lifting on hover.
        // The homepage opts in; other surfaces keep `default` until migrated.
        v2:      'min-h-[132px] rounded-[var(--radius-2xl)] border border-border bg-surface p-4 shadow-[var(--shadow-1)] transition-shadow hover:shadow-[var(--shadow-2)] sm:min-h-[152px] sm:p-5',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

// Tones resolve to the semantic theme tokens (warning/error/info/success), not
// Tailwind's built-in amber/rose/sky/emerald — so the meter bar and the value/
// pill share one red (one ochre, etc.) and re-theme together.
const toneChip: Record<KpiTone, string> = {
  neutral: 'bg-muted text-text-secondary',
  brand:   'bg-brand-100 text-brand-800',
  amber:   'bg-warning-surface text-warning-text',
  rose:    'bg-error-surface text-error-text',
  sky:     'bg-info-surface text-info-text',
  emerald: 'bg-success-surface text-success-text',
};

const meterFill: Record<NonNullable<KpiMeter['tone']>, string> = {
  brand:   'bg-brand-primary',
  amber:   'bg-warning',
  rose:    'bg-error',
  emerald: 'bg-success',
};

const meterStroke: Record<NonNullable<KpiMeter['tone']>, string> = {
  brand:   'text-brand-primary',
  amber:   'text-warning',
  rose:    'text-error',
  emerald: 'text-success',
};

function clamp(n: number) {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, n));
}

function MeterBar({ meter }: { meter: KpiMeter }) {
  const tone = meter.tone ?? 'brand';
  return (
    <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-muted" aria-hidden>
      <div className={cn('h-full rounded-full transition-[width]', meterFill[tone])} style={{ width: `${clamp(meter.value)}%` }} />
    </div>
  );
}

function MeterRing({ meter }: { meter: KpiMeter }) {
  const tone = meter.tone ?? 'brand';
  const pct = clamp(meter.value);
  const r = 14;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - pct / 100);
  return (
    <svg viewBox="0 0 36 36" className={cn('size-9 shrink-0', meterStroke[tone])} aria-hidden>
      <circle cx="18" cy="18" r={r} fill="none" stroke="currentColor" strokeOpacity="0.15" strokeWidth="3.5" />
      <circle
        cx="18" cy="18" r={r} fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={offset} transform="rotate(-90 18 18)"
      />
    </svg>
  );
}

export function KpiStat({
  icon: Icon,
  iconClass,
  tone = 'brand',
  variant,
  label,
  value,
  valueFont = 'mono',
  detail,
  hideDetailOnMobile,
  info,
  meter,
  href,
  onClick,
  ariaLabel,
  className,
  children,
}: KpiStatProps) {
  const chipCls = iconClass ?? toneChip[tone];
  const isV2 = variant === 'v2';
  // Signed sans values (a surplus/deficit, incl. signed money) take amount semantics: + green,
  // − brick. Unsigned ratios stay ink, and mono money is never colored (a "+R$" is currency, not a
  // gain signal). Mirrors the concept (green "+60%" surplus vs dark "14%" ratio). The deficit sign
  // may arrive as an ASCII hyphen, a Unicode minus (U+2212), or a leading "(" — match all three so a
  // server-formatted loss can never read as neutral ink.
  const signed = typeof value === 'string' ? value.trim() : '';
  const valueColor =
    valueFont !== 'sans'
      ? 'text-text-primary'
      : signed.startsWith('+')
        ? 'text-amount-positive'
        : signed.startsWith('-') || signed.startsWith('−') || signed.startsWith('(')
          ? 'text-amount-negative'
          : 'text-text-primary';

  const body = (
    <>
      <div className={cn('flex justify-between gap-1.5', isV2 ? 'items-center' : 'items-start')}>
        <div className="flex min-w-0 flex-1 items-center gap-1">
          <p className={cn(
            'leading-tight font-semibold wrap-break-word text-text-muted uppercase',
            isV2
              ? 'font-mono text-3xs tracking-[0.12em] sm:text-2xs sm:tracking-[0.14em]'
              : 'text-3xs tracking-[0.08em] sm:text-2xs sm:tracking-widest',
          )}>
            {label}
          </p>
          {info && <span className="inline-flex shrink-0 items-center leading-none">{info}</span>}
        </div>
        <span className={cn(
          'hidden shrink-0 place-items-center sm:grid',
          isV2 ? 'rounded-[10px] sm:size-10' : 'rounded-lg sm:size-7',
          chipCls,
        )}>
          <Icon size={isV2 ? 20 : 14} weight={isV2 ? 'regular' : 'duotone'} className="shrink-0" />
        </span>
      </div>

      <div className="mt-auto pt-3">
        <div className="flex items-end justify-between gap-3">
          <p
            className={cn(
              'min-w-0 tracking-tight wrap-anywhere tabular-nums',
              valueColor,
              valueFont === 'sans'
                ? 'text-3xl font-bold tracking-[-0.02em] sm:text-[2.375rem] sm:leading-none'
                : 'font-mono text-xl font-semibold sm:text-2xl',
            )}
          >
            {value}
          </p>
          {meter?.kind === 'ring' && <MeterRing meter={meter} />}
        </div>
        {detail && (
          <p
            className={cn('mt-1.5 text-sm text-text-secondary', hideDetailOnMobile && 'hidden sm:block')}
          >
            {detail}
          </p>
        )}
        {meter && meter.kind !== 'ring' && <MeterBar meter={meter} />}
        {children}
      </div>
    </>
  );

  const interactive = Boolean(href || onClick);
  const baseCls = cn(
    cardVariants({ variant }),
    interactive && 'hover:border-border-brand hover:shadow-md',
    className,
  );

  if (href) {
    return (
      <Link
        href={href}
        className="block h-full rounded-2xl outline-none focus-visible:ring-2 focus-visible:ring-brand-200/60 focus-visible:ring-offset-2"
      >
        <div className={baseCls}>{body}</div>
      </Link>
    );
  }

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={ariaLabel}
        className={cn(baseCls, 'w-full text-left outline-none focus-visible:ring-2 focus-visible:ring-brand-200/60 focus-visible:ring-offset-2')}
      >
        {body}
      </button>
    );
  }

  return <div className={baseCls}>{body}</div>;
}
